'use client';

import clsx from 'clsx';
import { memo, type ComponentPropsWithoutRef, type ElementType, type ReactNode } from 'react';

import MuiTypography from '@mui/material/Typography';

import styles from './Typography.module.scss';

export type UiTypographyTone = 'default' | 'muted' | 'accent' | 'danger';
export type UiTypographySize = 'eyebrow' | 'caption' | 'body' | 'body-lg' | 'title-sm' | 'title-md' | 'title-lg' | 'display';
export type UiTypographyWeight = 'regular' | 'medium' | 'semibold' | 'bold';

type TypographyOwnProps<T extends ElementType = 'p'> = {
    as?: T;
    children: ReactNode;
    className?: string;
    size?: UiTypographySize;
    tone?: UiTypographyTone;
    weight?: UiTypographyWeight;
    align?: 'left' | 'center' | 'right';
    balance?: boolean;
};

type TypographyProps<T extends ElementType> = TypographyOwnProps<T> & Omit<ComponentPropsWithoutRef<T>, keyof TypographyOwnProps<T>>;

const sizeClassNameMap: Record<UiTypographySize, string> = {
    eyebrow: styles.eyebrow,
    caption: styles.caption,
    body: styles.body,
    'body-lg': styles.bodyLarge,
    'title-sm': styles.titleSmall,
    'title-md': styles.titleMedium,
    'title-lg': styles.titleLarge,
    display: styles.display,
};

const toneClassNameMap: Record<UiTypographyTone, string> = {
    default: styles.default,
    muted: styles.muted,
    accent: styles.accent,
    danger: styles.danger,
};

const weightClassNameMap: Record<UiTypographyWeight, string> = {
    regular: styles.regular,
    medium: styles.medium,
    semibold: styles.semibold,
    bold: styles.bold,
};

const alignClassNameMap: Record<'left' | 'center' | 'right', string> = {
    left: styles.alignLeft,
    center: styles.alignCenter,
    right: styles.alignRight,
};

const TypographyComponent = <T extends ElementType = 'p'>({
    as,
    children,
    className = '',
    size = 'body',
    tone = 'default',
    weight = 'regular',
    align = 'left',
    balance = false,
    ...props
}: TypographyProps<T>) => {
    const Component = (as ?? 'p') as ElementType;

    return (
        <MuiTypography
            className={clsx(
                styles.type,
                sizeClassNameMap[size],
                toneClassNameMap[tone],
                weightClassNameMap[weight],
                alignClassNameMap[align],
                balance ? styles.balance : '',
                className
            )}
            component={Component}
            {...props}
        >
            {children}
        </MuiTypography>
    );
};

TypographyComponent.displayName = 'Typography';

export const Typography = memo(TypographyComponent) as typeof TypographyComponent;
